import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import { FormProvider, useForm } from 'react-hook-form';
import { Checkbox, Radio, FormControlLabel } from '@material-ui/core';
import { Check as CheckIcon } from '@material-ui/icons';

import NgpRadioGroup from '../../form/NgpRadioGroup';

import EnableDisableBox from '../../form/EnableDisableBox';

import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import RuleForm from './RuleForm';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import FormSection from '../../form/FormSection';
import NgpCheckbox from '../../form/NgpCheckbox';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS, MANUAL_ERROR_TYPE } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFormQueryService, useFind } from '../../../services/FormQueryService';
import ListFormField from '../../form/ListFormField';
import IpScopeForm from './IpScopeForm';
import { throwValidationError, useDataService } from '../../../services/ApiDataService';

import {
    FIELD_MAPPINGS,
    FIELD_PRIORITY_RULES,
    PollingApi,
    InterfaceRulesApi,
    MAX_INTF_SELECT_RULES,
} from './PollingConstants';

import { usePollingStyles } from './PollingStyles';
import PriorityInterfacePatternRule from './PriorityInterfacePatternRule';

const ErrorMessages = {
    oneIncludePatternRequired: 'At least one string is required',
    oneExcludePatternRequired: 'At least one string is required',
    onePriorityPatternRequired: 'At least one string is required',
    onePriorityPatternOrScopeRequired: '"Selected" choice requires at least one string or IP scope to be defined',
    onePriorityScopeRequired: 'At least one IP scope is required',
};

const getColumns = (fieldName) => {
    const columns = {
        [FIELD_MAPPINGS.INCLUDE_PATTERNS]: [
            {
                type: COLUMN_TYPES.TEXT,
                title: t`Step`,
                sortable: true,
                mapping: FIELD_MAPPINGS.INCLUDE_PATTERNS,
                flex: 1,
            },
        ],
        [FIELD_MAPPINGS.EXCLUDE_PATTERNS]: [
            {
                type: COLUMN_TYPES.TEXT,
                title: t`Step`,
                sortable: true,
                mapping: FIELD_MAPPINGS.EXCLUDE_PATTERNS,
                flex: 1,
            },
        ],
        [FIELD_MAPPINGS.PRIORITY_RULES_PATTERN]: [
            {
                type: COLUMN_TYPES.TEXT,
                title: t`Pattern`,
                sortable: true,
                mapping: FIELD_PRIORITY_RULES.PATTERN,
                width: 200,
            },
            {
                type: COLUMN_TYPES.ICON,
                mapping: FIELD_PRIORITY_RULES.MATCH_NAME,
                title: t`Name`,
                width: 200,
                icons: [
                    {
                        getIcon: (row) => (row[FIELD_PRIORITY_RULES.MATCH_NAME] ? <CheckIcon /> : null),
                    },
                ],
            },
            {
                type: COLUMN_TYPES.ICON,
                title: t`Description`,
                width: 200,
                icons: [
                    {
                        getIcon: (row) => (row[FIELD_PRIORITY_RULES.MATCH_DESCRIPTION] ? <CheckIcon /> : null),
                    },
                ],
            },
            {
                type: COLUMN_TYPES.ICON,
                title: t`Type`,
                width: 200,
                icons: [
                    {
                        getIcon: (row) => (row[FIELD_PRIORITY_RULES.MATCH_TYPE] ? <CheckIcon /> : null),
                    },
                ],
            },
        ],
        [FIELD_MAPPINGS.PRIORITY_RULES_IP_SCOPES]: [
            {
                type: COLUMN_TYPES.TEXT,
                title: t`IP Scope`,
                sortable: true,
                mapping: 'value.subnet',
                width: 250,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: t`Type`,
                mapping: 'exclude',
                formatter: (value) => (value === false ? t`Included` : t`Excluded`),
                width: 250,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: t`Description`,
                sortable: true,
                mapping: 'description',
                width: 210,
            },
        ],
    };
    return columns[fieldName] || [];
};

const defaultFormValues = {
    [FIELD_MAPPINGS.INCLUDE_PATTERNS]: [],
    [FIELD_MAPPINGS.EXCLUDE_PATTERNS]: [],
    [FIELD_MAPPINGS.ANY_INCLUDED]: true,
    [FIELD_MAPPINGS.PATTERN_EXCLUDED]: false,
    [FIELD_MAPPINGS.PRIORITY_RULES_ALL]: false,
    priorityRules: {
        patterns: [],
        ipScopes: [],
    },
};

function prepareSavedData(dataToSave, formData) {
    const putData = _.merge({}, formData, dataToSave);
    return {
        ...putData,
        updatedAt: new Date().toISOString(),
        includedPatterns: _.map(putData.includedPatterns, (pattern) => pattern.includedPatterns),
        excludedPatterns: _.map(putData.excludedPatterns, (pattern) => pattern.excludedPatterns),
        //     priorityRules: {
        //         ...putData.priorityRules,
        //         patterns: _.map(putData.priorityRules.patterns, (pattern) => pattern[FIELD_MAPPINGS.PRIORITY_RULES_PATTERN]),
        //         ipScopes: _.map(putData.priorityRules.ipScopes, (scope) => scope[FIELD_MAPPINGS.IP_SCOPES]),
        //     }
        // }
    };
}

const InterfaceSelectionTab = forwardRef((props, ref) => {
    const [deviceEnabled, setDeviceEnabled] = useState(true);
    const classes = usePollingStyles();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: defaultFormValues,
        mode: 'onChange',
    });
    const [configIsDirty, setConfigIsDirty] = useState(false);
    const { reset, setError, watch } = methods;
    const anyIncluded = watch(FIELD_MAPPINGS.ANY_INCLUDED);
    const patternExcluded = watch(FIELD_MAPPINGS.PATTERN_EXCLUDED);
    const priorityRulesAll = watch(FIELD_MAPPINGS.PRIORITY_RULES_ALL);
    const priorityRulesPatternIncluded = watch(FIELD_MAPPINGS.PRIORITY_RULES_PATTERN_INCLUDED);
    const ipScopesIncluded = watch(FIELD_MAPPINGS.PRIORITY_RULES_IP_SCOPES_INCLUDED);
    const dataService = useDataService();
    const formSaveService = useFormSaveService();
    const formQueryService = useFormQueryService();

    useEffect(() => {
        formQueryService.queryAPI(PollingApi).then((pollingConfig) => {
            setDeviceEnabled(_.get(pollingConfig, FIELD_MAPPINGS.DEVICE_ENABLED, false));
        });
        // Only want to run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const postFindHandler = (response) => {
        const _response = response[0] || _.first(response) || response;
        const cleaned = _.cloneDeep(_response) || {};
        [FIELD_MAPPINGS.INCLUDE_PATTERNS, FIELD_MAPPINGS.EXCLUDE_PATTERNS].forEach((field) => {
            cleaned[field] = _.map(cleaned[field], (pattern) => ({
                [field]: pattern,
            }));
        });
        return cleaned;
    };

    const { initialized, finding, formData, resetForm } = useFind({
        url: InterfaceRulesApi,
        postFindHandler,
        resetFunc: reset,
        defaultValues: defaultFormValues,
        errorHandler: errorWrapper({}),
    });

    const handleFormSubmit = (dataToSave = {}) => {
        const validations = [
            {
                cond:
                    !dataToSave[FIELD_MAPPINGS.ANY_INCLUDED] &&
                    _.isEmpty(_.get(dataToSave, FIELD_MAPPINGS.INCLUDE_PATTERNS)),
                field: FIELD_MAPPINGS.INCLUDE_PATTERNS,
                message: ErrorMessages.oneIncludePatternRequired,
            },
            {
                cond:
                    dataToSave[FIELD_MAPPINGS.PATTERN_EXCLUDED] &&
                    _.isEmpty(_.get(dataToSave, FIELD_MAPPINGS.EXCLUDE_PATTERNS)),
                field: FIELD_MAPPINGS.EXCLUDE_PATTERNS,
                message: ErrorMessages.oneExcludePatternRequired,
            },
            {
                cond:
                    dataToSave[FIELD_MAPPINGS.PRIORITY_RULES_PATTERN_INCLUDED] &&
                    _.isEmpty(_.get(dataToSave, FIELD_MAPPINGS.PRIORITY_RULES_PATTERN)),
                field: FIELD_MAPPINGS.PRIORITY_RULES_PATTERN,
                message: ErrorMessages.onePriorityPatternRequired,
            },
            {
                cond:
                    dataToSave[FIELD_MAPPINGS.PRIORITY_RULES_IP_SCOPES_INCLUDED] &&
                    _.isEmpty(_.get(dataToSave, FIELD_MAPPINGS.PRIORITY_RULES_IP_SCOPES)),
                field: FIELD_MAPPINGS.PRIORITY_RULES_IP_SCOPES,
                message: ErrorMessages.onePriorityScopeRequired,
            },
        ];

        validations.forEach((v) => {
            if (v.cond) {
                setError(v.field, {
                    type: MANUAL_ERROR_TYPE,
                    message: v.message,
                });
                return throwValidationError();
            }
        });

        const putData = prepareSavedData(dataToSave, formData);
        const putUrl = `${InterfaceRulesApi}/${formData._id}`;

        return formSaveService.save({
            url: putUrl,
            saveFunc: () =>
                dataService.put(putUrl, putData).then(() => {
                    setConfigIsDirty(false);
                    resetForm();
                }),
            dataToSave: putData,
            setError,
        });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);

    const dirtyCheck = (isDirty) => isDirty || configIsDirty;

    useImperativeHandle(ref, () => ({
        save: methods.handleSubmit(handleSubmitFunction),
    }));

    if (initialized && !deviceEnabled) {
        return (
            <div className={classes.interfaceRuleMessage}>
                {t`Please be noted 'Monitor network devices' is disabled in Polling setting!`}
            </div>
        );
    }
    // debug removed

    return (
        <FormProvider {...methods}>
            <NgpForm
                submitFunction={handleSubmitFunction}
                initialized={initialized}
                loading={finding || saving}
                isDirtyFunc={dirtyCheck}
                showRequiredFieldsTip={false}
            >
                <FormSection title={<Trans>Monitored Interface</Trans>}>
                    <NgpRadioGroup fieldName={FIELD_MAPPINGS.ANY_INCLUDED}>
                        <FormControlLabel value={'true'} label={t`All`} control={<Radio />} />
                        <FormControlLabel
                            value={'false'}
                            label={t`With a name or description containing any of the following`}
                            control={<Radio />}
                        />
                    </NgpRadioGroup>
                    <EnableDisableBox disabled={anyIncluded}>
                        <ListFormField
                            fieldName={FIELD_MAPPINGS.INCLUDE_PATTERNS}
                            dbSelected={_.get(formData, FIELD_MAPPINGS.INCLUDE_PATTERNS)}
                            addButtonText={t`ADD STRING`}
                            gridColumns={getColumns(FIELD_MAPPINGS.INCLUDE_PATTERNS)}
                            formComponent={<RuleForm fieldName={FIELD_MAPPINGS.INCLUDE_PATTERNS} />}
                            setSelectionIsDirty={setConfigIsDirty}
                            showHeaders={false}
                            visibleRows={MAX_INTF_SELECT_RULES + 1}
                            emptyGridText={t`Click the Add String button to add new string`}
                        />
                    </EnableDisableBox>
                </FormSection>
                <FormSection title={<Trans>Excluded Interfaces</Trans>}>
                    <NgpCheckbox fieldName={FIELD_MAPPINGS.LOOPBACK_EXCLUDED} label={<Trans>Loopback</Trans>} />
                    <NgpCheckbox
                        fieldName={FIELD_MAPPINGS.ADMINDOWN_EXCLUDED}
                        label={<Trans>With an admin status of down</Trans>}
                    />
                    <NgpCheckbox
                        fieldName={FIELD_MAPPINGS.PATTERN_EXCLUDED}
                        label={<Trans>With a name or description containing any of the following</Trans>}
                    />
                    <EnableDisableBox disabled={!patternExcluded}>
                        <ListFormField
                            fieldName={FIELD_MAPPINGS.EXCLUDE_PATTERNS}
                            dbSelected={_.get(formData, FIELD_MAPPINGS.EXCLUDE_PATTERNS)}
                            addButtonText={t`ADD STRING`}
                            gridColumns={getColumns(FIELD_MAPPINGS.EXCLUDE_PATTERNS)}
                            formComponent={<RuleForm fieldName={FIELD_MAPPINGS.EXCLUDE_PATTERNS} />}
                            setSelectionIsDirty={setConfigIsDirty}
                            showHeaders={false}
                            visibleRows={MAX_INTF_SELECT_RULES + 1}
                            emptyGridText={t`Click the Add String button to add new string`}
                        />
                    </EnableDisableBox>
                </FormSection>
                <FormSection title={<Trans>Priority Interfaces</Trans>}>
                    <NgpRadioGroup fieldName={FIELD_MAPPINGS.PRIORITY_RULES_ALL}>
                        <FormControlLabel value={'false'} label={t`All`} control={<Radio />} />
                        <FormControlLabel value={'true'} label={t`Selected`} control={<Radio />} />
                    </NgpRadioGroup>
                    <EnableDisableBox disabled={!priorityRulesAll} style={{ marginLeft: '30px' }}>
                        {/* In portal/ui this field 'wanPriority' is non-editable; default true */}
                        <FormControlLabel
                            control={<Checkbox checked disabled />}
                            label={<Trans>WAN interfaces</Trans>}
                        />
                        <NgpCheckbox
                            fieldName={FIELD_MAPPINGS.PRIORITY_RULES_PATTERN_INCLUDED}
                            label={<Trans>With a name, description, or type containing any of the following</Trans>}
                        />
                        <EnableDisableBox disabled={!priorityRulesPatternIncluded}>
                            <ListFormField
                                fieldName={FIELD_MAPPINGS.PRIORITY_RULES_PATTERN}
                                dbSelected={_.get(formData, FIELD_MAPPINGS.PRIORITY_RULES_PATTERN)}
                                addButtonText={t`ADD STRING`}
                                gridColumns={getColumns(FIELD_MAPPINGS.PRIORITY_RULES_PATTERN)}
                                formComponent={<PriorityInterfacePatternRule />}
                                visibleRows={11}
                                setSelectionIsDirty={setConfigIsDirty}
                                emptyGridText={t`Click the Add String button to add new string`}
                            />
                        </EnableDisableBox>
                        <NgpCheckbox
                            fieldName={FIELD_MAPPINGS.PRIORITY_RULES_IP_SCOPES_INCLUDED}
                            label={<Trans>Within the following scopes</Trans>}
                        />
                        <EnableDisableBox disabled={!ipScopesIncluded}>
                            <ListFormField
                                fieldName={FIELD_MAPPINGS.PRIORITY_RULES_IP_SCOPES}
                                dbSelected={_.get(formData, FIELD_MAPPINGS.PRIORITY_RULES_IP_SCOPES)}
                                addButtonText={t`ADD SCOPE`}
                                gridColumns={getColumns(FIELD_MAPPINGS.PRIORITY_RULES_IP_SCOPES)}
                                formComponent={<IpScopeForm />}
                                visibleRows={21}
                                setSelectionIsDirty={setConfigIsDirty}
                                emptyGridText={t`Click the Add Scope button to add new scope`}
                            />
                        </EnableDisableBox>
                    </EnableDisableBox>
                    <NgpCheckbox
                        fieldName={FIELD_MAPPINGS.CRITICAL_STATUS_ENABLED}
                        label={<Trans>Priority interfaces are Critical when operationally down</Trans>}
                        helpTipText={t`Priority interface rules prevent unwanted Critical status`}
                    />
                </FormSection>
            </NgpForm>
        </FormProvider>
    );
});

export default InterfaceSelectionTab;
